/**
 * bakery-delivery — computes the arrival date for a direct-shipping obrador.
 *
 * Liquid renders the schedule (true indefinitely, safe to edge-cache); this
 * fills in the dated sentence, which is only correct at the moment it runs.
 * See the comment block in snippets/bakery-delivery.liquid for why the split
 * exists — a cached "te llega el martes" served on Wednesday is a broken
 * promise on a fresh-food product.
 *
 * Arrival = next bake day at/after now (respecting the cutoff)
 *           + N business days in transit.
 * Transit skips weekends because couriers do; a Thursday dispatch with 2
 * transit days lands Monday, not Saturday.
 */
(function () {
  'use strict';

  var DAYS = {
    domingo: 0, lunes: 1, martes: 2, miercoles: 3, 'miércoles': 3,
    jueves: 4, viernes: 5, sabado: 6, 'sábado': 6,
  };

  var MONTHS = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
  ];

  var DAY_NAMES = [
    'domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado',
  ];

  function normalise(value) {
    return String(value).trim().toLowerCase();
  }

  /**
   * "Now" in the bakery's own timezone, as a plain {y,m,d,minutes} record.
   * The bakery's clock decides whether the cutoff has passed — a shopper in
   * the Canaries ordering at 09:30 local has NOT missed a 09:00 Madrid
   * cutoff, they are an hour behind it. Using the browser clock would move
   * the cutoff around by the shopper's location, which is why zona_horaria
   * exists as a field at all.
   */
  function nowInZone(timeZone) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', weekday: 'short', hour12: false,
      }).formatToParts(new Date());
    } catch (err) {
      return null; // unknown timezone — fail closed, leave the schedule alone
    }

    var get = function (type) {
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === type) return parts[i].value;
      }
      return null;
    };

    var hour = parseInt(get('hour'), 10);
    // Intl can emit hour "24" for midnight under hour12:false in some engines.
    if (hour === 24) hour = 0;

    var date = new Date(Date.UTC(
      parseInt(get('year'), 10),
      parseInt(get('month'), 10) - 1,
      parseInt(get('day'), 10)
    ));

    return { date: date, minutes: hour * 60 + parseInt(get('minute'), 10) };
  }

  function parseCutoff(raw) {
    if (!raw) return null;
    var m = String(raw).match(/^(\d{1,2})[:.h](\d{2})$/);
    if (!m) return null;
    var h = parseInt(m[1], 10);
    var min = parseInt(m[2], 10);
    if (h > 23 || min > 59) return null;
    return h * 60 + min;
  }

  function addDays(date, n) {
    var d = new Date(date.getTime());
    d.setUTCDate(d.getUTCDate() + n);
    return d;
  }

  /** Advance N business days, skipping Sat/Sun. */
  function addBusinessDays(date, n) {
    var d = date;
    var left = n;
    while (left > 0) {
      d = addDays(d, 1);
      var wd = d.getUTCDay();
      if (wd !== 0 && wd !== 6) left--;
    }
    return d;
  }

  function formatDate(date) {
    return DAY_NAMES[date.getUTCDay()] + ' ' + date.getUTCDate() +
      ' de ' + MONTHS[date.getUTCMonth()];
  }

  /**
   * The two legs of the journey, as plain dates.
   *
   * Exposed separately from render() so it can be tested without a DOM, and
   * so the step-by-step disclosure can name the dispatch date rather than
   * only the arrival. Returns null whenever the promise cannot be made.
   */
  function computeLegs(opts) {
    if (!opts.bakeDays.length) return null;
    var dispatch = null;
    for (var i = 0; i <= 7; i++) {
      var candidate = addDays(opts.now.date, i);
      if (opts.bakeDays.indexOf(candidate.getUTCDay()) === -1) continue;
      // Today only counts if it is a bake day AND the cutoff has not passed.
      // With no cutoff recorded, today does not count: assuming an order at
      // 23:50 makes that morning's bake is the optimistic guess, and this
      // promise fails closed.
      if (i === 0 && (opts.cutoffMinutes === null || opts.now.minutes >= opts.cutoffMinutes)) continue;
      dispatch = candidate;
      break;
    }
    if (!dispatch) return null;
    return {
      dispatch: dispatch,
      arrival: opts.transit > 0 ? addBusinessDays(dispatch, opts.transit) : dispatch
    };
  }

  function render(el) {
    var live = el.querySelector('[data-bakery-delivery-live]');
    if (!live) return;

    var bakeDays = (el.dataset.bakeDays || '')
      .split(',')
      .map(normalise)
      .map(function (d) { return DAYS[d]; })
      .filter(function (d) { return typeof d === 'number'; });

    if (!bakeDays.length) return;

    var template = el.dataset.liveTemplate || '';
    if (template.indexOf('@@FECHA@@') === -1) return;

    var now = nowInZone(el.dataset.timezone || 'Europe/Madrid');
    if (!now) return;

    // Fails closed at 0, not just below it. The Liquid turns a MISSING
    // `dias_hasta_envio` into 0 (`| default: 0`), so 0 means "not recorded"
    // far more often than it means "arrives the day it is baked" — and for a
    // parcel drop-shipped from the obrador, same-day dispatch AND arrival is
    // never a safe assumption to publish. With no transit on record we render
    // nothing and let the undated schedule stand, which never expires.
    var transit = parseInt(el.dataset.transit, 10);
    if (isNaN(transit) || transit < 1) return;

    var cutoff = parseCutoff(el.dataset.cutoff);

    var legs = computeLegs({
      bakeDays: bakeDays,
      cutoffMinutes: cutoff,
      transit: transit,
      now: now
    });
    if (!legs) return;
    var arrival = legs.arrival;

    live.textContent = template.replace('@@FECHA@@', formatDate(arrival));
    live.hidden = false;

    // Hands the lead treatment from the schedule to the dated line. Only ever
    // set here, after a date has actually been computed — so a failed or
    // skipped render leaves the schedule as the lead, which is correct.
    el.classList.add('bakery-delivery--live');

    var steps = el.querySelector('[data-bakery-delivery-steps]');
    if (steps) {
      var whenDispatch = steps.querySelector('[data-step-when="dispatch"]');
      // The arrival step is omitted entirely when transit is 0, so it is
      // optional here — the disclosure still stands on the first two steps.
      var whenArrival = steps.querySelector('[data-step-when="arrival"]');
      if (whenDispatch) {
        whenDispatch.textContent = formatDate(legs.dispatch);
        if (whenArrival) whenArrival.textContent = formatDate(legs.arrival);
        steps.hidden = false;
      }
    }
  }

  function init() {
    var nodes = document.querySelectorAll('[data-bakery-delivery]');
    for (var i = 0; i < nodes.length; i++) render(nodes[i]);
  }

  // The storefront always has `document`; this guard exists purely so
  // assets/bakery-delivery.test.js can `require()` this file under Node —
  // without it, requiring the module runs the auto-init below immediately
  // and throws on the first DOM call before the Node export below is ever
  // reached. Inert in the browser.
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    // Variant switches re-render the PDP block in Dawn.
    document.addEventListener('shopify:section:load', init);
  }

  // Node-only, for assets/bakery-delivery.test.js. The browser has no
  // `module`, so this is inert on the storefront.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { computeLegs: computeLegs, addBusinessDays: addBusinessDays };
  }
})();
